/**
 * EquipmentModal — assigns key, radio, recorder, posts, and shift times to
 * a single personnel entry.
 *
 * Opens from OfficerCard's "Edit Equipment" link. Closes via cancel,
 * save, or click-outside (which auto-saves to mirror FOM behaviour).
 *
 * Filters available equipment based on whether the slot is a supervisor:
 *   - Supervisors get supervisor radios and recorders 2/3
 *   - Officers get officer radios and recorders other than 2/3
 */
import { useState } from 'react'
import { useDesktopStore, type PersonnelEntry, type Post } from '../../stores/desktop'
import { useAuthStore } from '../../stores/auth'
import { markDirty } from '../../stores/sync'
import { saveEquipmentAssignment } from '../../stores/postingBridge'
import PostIcon from './PostIcon'

/** Secondary task definitions — mirrors FOM's jn array */
const SECONDARY_TASKS = [
  { task: 'Fire Extinguishers', label: 'FE Upper', detail: 'Fire Extinguishers - Upper Level' },
  { task: 'Fire Extinguishers', label: 'FE Lower', detail: 'Fire Extinguishers - Lower Level' },
  { task: 'Mall Inspection', label: 'Detex', detail: 'Detex' },
  { task: 'Mall Doors', label: 'Mall Doors', detail: 'Mall Doors' },
  { task: 'Escalators', label: 'Esc/TV', detail: 'Escalators/TV' },
  { task: 'Service Doors', label: 'Svc Doors', detail: 'Service Door/J Ext' },
  { task: 'J Ext', label: 'J Ext', detail: 'J Ext' },
  { task: 'Chinese Lunar New Year', label: 'Lunar New Year', detail: 'Chinese Lunar New Year' },
]

/**
 * Auto-fill time for a secondary task by its LABEL + shift + day of week.
 * Returns HH:MM (colon format, matching this modal's time input), or '' when
 * there's no rule (Night, Lunar New Year, unmatched). Pre-filled into the time
 * box when the task is shown; the user can still edit before adding.
 *
 * Rules (ET day of week):
 *   Morning: Mall Doors, FE Upper, FE Lower, Detex → 07:00 ; Esc/TV → 09:30
 *   Evening: FE Upper, FE Lower → 15:00 (all days)
 *            Mall Doors, Esc/TV → 20:00 Mon–Thu / 21:00 Fri–Sat / 18:00 Sun
 *            Svc Doors, J Ext   → 21:00 Mon–Thu / 22:00 Fri–Sat / 19:00 Sun
 *   Night: blank for now.
 */
function autoTaskTimeByLabel(label: string): string {
  // Current shift from ET time
  let h: number
  try {
    h = parseInt(new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', hour12: false }).split(':')[0])
  } catch { h = new Date().getHours() }
  const shift = h >= 7 && h < 15 ? 'Morning' : h >= 15 && h < 23 ? 'Evening' : 'Night'
  // Day of week in ET
  let dow = new Date().getDay()
  try {
    const etDay = new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'short' })
    const map: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
    if (etDay in map) dow = map[etDay as keyof typeof map]
  } catch { /* fall back */ }
  const isFriSat = dow === 5 || dow === 6
  const isSun = dow === 0

  if (shift === 'Morning') {
    if (['Mall Doors', 'FE Upper', 'FE Lower', 'Detex'].includes(label)) return '07:00'
    if (label === 'Esc/TV') return '09:30'
    return ''
  }
  if (shift === 'Evening') {
    if (label === 'FE Upper' || label === 'FE Lower') return '15:00'
    if (label === 'Mall Doors' || label === 'Esc/TV') return isFriSat ? '21:00' : isSun ? '18:00' : '20:00'
    if (label === 'Svc Doors' || label === 'J Ext') return isFriSat ? '22:00' : isSun ? '19:00' : '21:00'
    return ''
  }
  return ''
}

/**
 * Compute the current shift's time bounds from Eastern Time — mirrors FOM's
 * `getCurrentShift()` plus the post-time defaulting baked into FOM's addPost.
 *
 * Used to pre-fill post assignment from/to times. If a supervisor adds a post
 * during the Evening shift (15:00–23:00 ET), the new post starts pre-filled
 * with from=1500 to=2300. They can adjust the dropdowns afterward; this just
 * removes the busy-work of typing the most common case (post spans the shift).
 *
 * Returns HHMM format (no colon) to match the values used by the post time
 * <select> options elsewhere in this modal.
 */
function getCurrentShiftBounds(): { from: string; to: string } {
  let h: number
  try {
    h = parseInt(
      new Date()
        .toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', hour12: false })
        .split(':')[0],
    )
  } catch {
    h = new Date().getHours()
  }
  if (h >= 7 && h < 15)  return { from: '0700', to: '1500' }  // Morning
  if (h >= 15 && h < 23) return { from: '1500', to: '2300' }  // Evening
  return { from: '2300', to: '0700' }                          // Night (wraps midnight)
}

/**
 * Build the list of HHMM time options that fall within the current shift,
 * in 30-minute increments. Used by the post-from/to <select> dropdowns so
 * a Morning supervisor only sees 0700, 0730, 0800... 1500.
 *
 * Night shift wraps midnight, so we render 2300, 2330, 0000, 0030, ... 0700
 * in chronological order — not numeric — so dropdown traversal matches the
 * timeline operators are thinking in.
 *
 * 30-minute granularity is intentional. Officers occasionally need partial
 * post coverage (sick, sudden shift change, late arrival) and the half-hour
 * resolution captures those without overwhelming the dropdown with 96
 * 15-minute slots.
 */
function getShiftTimeOptions(): string[] {
  const { from, to } = getCurrentShiftBounds()
  const fromMins = parseInt(from.slice(0, 2)) * 60 + parseInt(from.slice(2))
  let toMins = parseInt(to.slice(0, 2)) * 60 + parseInt(to.slice(2))
  // Night shift: 2300 to 0700 means we wrap midnight. Add 24h to the end so
  // the loop walks chronologically across the boundary.
  if (toMins <= fromMins) toMins += 24 * 60
  const out: string[] = []
  for (let m = fromMins; m <= toMins; m += 30) {
    const wrapped = m % (24 * 60)
    const h = Math.floor(wrapped / 60)
    const min = wrapped % 60
    out.push(String(h).padStart(2, '0') + String(min).padStart(2, '0'))
  }
  return out
}

interface TaskEntry {
  callSign: string
  task: string
  label: string
  detail: string
  time: string
}

interface EquipmentModalProps {
  entry: PersonnelEntry
  index: number
  onClose: () => void
}

export default function EquipmentModal({ entry, index, onClose }: EquipmentModalProps) {
  const { securityKeys, flashlights, supRadios, offRadios, otherRadios, recorders, secondaryTasks, updateOfficer } = useDesktopStore()
  const setSecondaryTasks = useDesktopStore(s => s.setSecondaryTasks)
  const accessLevel = useAuthStore(s => {
    const sess = s.session
    if (!sess) return 'officer'
    if (sess.accessLevel) return sess.accessLevel
    const r = (sess.rank || '').toLowerCase()
    if (r === 'developer' || r === 'dv') return 'developer'
    if (r.includes('director') || r.includes('assistant') || r.includes('lieut') || r.includes('lt.')) return 'exec'
    if (r.includes('serg') || r.includes('sgt') || r.includes('oic')) return 'supervisor'
    return 'officer'
  })
  const isOfficer = accessLevel === 'officer'

  // SGT radios 95/97/99 live in otherRadios — add them to the supervisor picker
  const SGT_RADIO_IDS = ['95', '97', '99']
  const SGT_LABELS: Record<string, string> = { '95': 'S-01', '97': 'S-03', '99': 'S-02' }
  const sgtRadios = otherRadios
    .filter(r => SGT_RADIO_IDS.includes(r.id))
    .map(r => ({ ...r, assignedTo: r.assignedTo || SGT_LABELS[r.id] }))
  const supRadiosWithSgt = [...sgtRadios, ...supRadios.filter(r => !SGT_RADIO_IDS.includes(r.id))]

  const [key, setKey]             = useState(entry.key)
  const [radio, setRadio]         = useState(entry.radio)
  const [recorder, setRecorder]   = useState(entry.recorder)
  const [flashlight, setFlashlight] = useState(entry.flashlight)
  const [posts, setPosts]         = useState<Post[]>(entry.posts)
  const [shiftFrom, setShiftFrom] = useState(entry.shiftFrom)
  const [shiftTo, setShiftTo]     = useState(entry.shiftTo)

  // Secondary tasks for this officer
  const csNorm = (cs: string) => {
    if (!cs) return ''
    const m = cs.match(/^([A-Za-z]+-)(\d+)$/)
    return m ? m[1] + parseInt(m[2], 10) : cs
  }
  const identifier = entry.callSign || entry.name
  const myTasks = secondaryTasks.filter(
    (t: TaskEntry) => t.callSign && identifier && csNorm(t.callSign) === csNorm(identifier),
  )

  const isSup = ['Dir/AD/Lt', 'Sergeant', 'OIC', 'Developer'].includes(entry.role)
  const name  = entry.name

  // Available pools — filtered by current assignment + slot role
  const availKeys = securityKeys.filter(k => !k.assignedTo || k.assignedTo === name)

  // All radios for dropdown — show assigned ones greyed out
  const allRadiosForPicker = isSup ? supRadiosWithSgt : offRadios

  const availRecs = recorders.filter(r =>
    !r.oos &&
    (r.available || r.assignedTo === name) &&
    (isSup ? [2, 3].includes(Number(r.id)) : ![2, 3].includes(Number(r.id))),
  )

  const availFlashlights = flashlights.filter(f =>
    !f.permanent && (f.available || f.assignedTo === name)
  )

  function addPost(postName: string) {
    if (!postName) return
    // Break keeps its own self-managed times (start + 30 min auto-end)
    if (postName === 'Break') {
      setPosts(prev => [
        ...prev,
        { post: postName, from: '', to: '', isBreak: true },
      ])
      return
    }
    // Non-break posts default to the current shift's bounds. Supervisor can
    // override via the from/to dropdowns. Matches FOM's behavior of treating
    // the most common case (post spans the entire shift) as the default.
    const { from, to } = getCurrentShiftBounds()
    setPosts(prev => [
      ...prev,
      { post: postName, from, to, isBreak: false },
    ])
  }

  function removePost(i: number) {
    setPosts(prev => prev.filter((_, idx) => idx !== i))
  }

  function updatePostTime(i: number, field: 'from' | 'to', val: string) {
    setPosts(prev =>
      prev.map((p, idx) => {
        if (idx !== i) return p
        const updated = { ...p, [field]: val }
        // Auto-calculate break end time (+30 min)
        if (p.isBreak && field === 'from' && val) {
          const h = parseInt(val.slice(0, 2))
          const m = parseInt(val.slice(2))
          const total = (h * 60 + m + 30) % (24 * 60)
          updated.to =
            String(Math.floor(total / 60)).padStart(2, '0') +
            String(total % 60).padStart(2, '0')
        }
        return updated
      }),
    )
  }

  function handleShiftFrom(raw: string) {
    const digits = raw.replace(/\D/g, '').slice(0, 4)
    let out = digits
    if (digits.length >= 3) out = digits.slice(0, 2) + ':' + digits.slice(2)
    setShiftFrom(out)
  }

  function handleShiftTo(raw: string) {
    const digits = raw.replace(/\D/g, '').slice(0, 4)
    let out = digits
    if (digits.length >= 3) out = digits.slice(0, 2) + ':' + digits.slice(2)
    setShiftTo(out)
  }

  function save() {
    markDirty()
    updateOfficer(index, { key, radio, recorder, flashlight, posts, shiftFrom, shiftTo })
    // Persist equipment assignment so it survives posting merges
    if (entry.callSign) {
      saveEquipmentAssignment(entry.callSign, { key, radio, recorder })
    }
    // Persist secondary tasks to localStorage so they sync
    try { localStorage.setItem('fom_secondaryTasks', JSON.stringify(secondaryTasks)) } catch {}
    onClose()
  }

  function addTask(taskDef: typeof SECONDARY_TASKS[number], time: string) {
    const cs = entry.callSign || entry.name
    if (!cs) return
    const already = secondaryTasks.some(
      (t: TaskEntry) => t.label === taskDef.label && csNorm(t.callSign) === csNorm(cs),
    )
    if (already) return
    const detail = time ? `${taskDef.detail} @ ${time}` : taskDef.detail
    const newTask: TaskEntry = { callSign: cs, task: taskDef.task, label: taskDef.label, detail, time: time || '' }
    setSecondaryTasks([...secondaryTasks, newTask])
  }

  function removeTask(label: string) {
    const cs = csNorm(entry.callSign || entry.name)
    setSecondaryTasks(secondaryTasks.filter(
      (t: TaskEntry) => !(csNorm(t.callSign) === cs && t.label === label),
    ))
  }

  const inp: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 14,
    outline: 'none',
    background: 'var(--bg-deep)',
    color: 'var(--text-primary)',
    marginTop: 4,
    colorScheme: 'dark' as React.CSSProperties['colorScheme'],
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: 16,
      }}
      onClick={e => e.target === e.currentTarget && save()}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          border: '1px solid var(--border)',
          padding: 24,
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          overflowY: 'auto',
          color: 'var(--text-primary)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>
            Assign Equipment to {entry.name}
          </h3>
          <button
            onClick={save}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}
          >
            ✕
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Security Key */}
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              Security Key
            </label>
            <select value={key} onChange={e => setKey(e.target.value)} style={inp}>
              <option value="">Select Key...</option>
              {availKeys.map(k => (
                <option key={k.number} value={String(k.number)}>
                  Key {k.number}
                </option>
              ))}
            </select>
          </div>

          {/* Radio */}
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              Radio
            </label>
            <select value={radio} onChange={e => setRadio(e.target.value)} style={inp}>
              <option value="">Select Radio...</option>
              {allRadiosForPicker.map(r => {
                const taken = !!r.assignedTo && r.assignedTo !== name && !r.available
                const isAssigned = r.assignedTo === name
                return (
                  <option
                    key={r.id}
                    value={r.id}
                    disabled={taken}
                    style={{ color: taken ? '#6b7280' : isAssigned ? '#34d399' : 'inherit' }}
                  >
                    {isAssigned ? '✓ ' : taken ? '✗ ' : ''}Radio {r.id}{taken ? ` (${r.assignedTo})` : ''}
                  </option>
                )
              })}
            </select>
          </div>

          {/* Recorder */}
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              Digital Recorder
            </label>
            <select value={recorder} onChange={e => setRecorder(e.target.value)} style={inp}>
              <option value="">Select Recorder...</option>
              {availRecs.map(r => (
                <option key={r.id} value={String(r.id)}>
                  Recorder {r.id}
                </option>
              ))}
            </select>
          </div>

          {/* Flashlight assignment removed from this modal per Apr 2026 spec.
              State and save path are preserved (flashlight, setFlashlight, save())
              so existing flashlight data is not destroyed if anyone has it set,
              and so flashlight assignment can return as a separate workflow later. */}

          {/* Posts — read-only for officers, editable for supervisors */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', display: 'block', marginBottom: 8 }}>
              Post Assignment {isOfficer && <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(view only)</span>}
            </label>
            {/* Only supervisors can add/change posts */}
            {!isOfficer && (
              <select value="" onChange={e => addPost(e.target.value)} style={{ ...inp, marginTop: 0 }}>
                <option value="">Select Post to Add...</option>
                <option value="Mall">Mall</option>
                <option value="Vehicle">Vehicle</option>
                <option value="Desk">Desk</option>
                <option value="Special">Special</option>
                <option value="Break">Break (30 min)</option>
              </select>
            )}

            {posts.length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {posts.map((p, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: (p.isBreak || p.post === 'Break') ? '#e5ad76' : 'var(--bg-elevated)',
                      borderRadius: 8,
                      padding: '6px 10px',
                    }}
                  >
                    <PostIcon post={p.post} isBreak={p.isBreak} />
                    <span style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>{p.post}</span>
                    <select
                      value={p.from}
                      onChange={e => updatePostTime(i, 'from', e.target.value)}
                      disabled={isOfficer}
                      style={{
                        border: '1px solid var(--border)',
                        borderRadius: 6,
                        padding: '2px 4px',
                        fontSize: 12,
                        width: 70,
                        background: 'var(--bg-deep)',
                        color: 'var(--text-primary)',
                        colorScheme: 'dark' as React.CSSProperties['colorScheme'],
                        pointerEvents: isOfficer ? 'none' : 'auto',
                      }}
                    >
                      <option value="">From</option>
                      {getShiftTimeOptions().map(v => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                    <span style={{ color: 'var(--text-muted)' }}>-</span>
                    {(p.isBreak || p.post === 'Break') ? (
                      <span style={{ fontSize: 12, color: 'var(--text-muted)', width: 70, textAlign: 'center' }}>
                        {p.to || '+30m'}
                      </span>
                    ) : (
                      <select
                        value={p.to}
                        onChange={e => updatePostTime(i, 'to', e.target.value)}
                        disabled={isOfficer}
                        style={{
                          border: '1px solid var(--border)',
                          borderRadius: 6,
                          padding: '2px 4px',
                          fontSize: 12,
                          width: 70,
                          background: 'var(--bg-deep)',
                          color: 'var(--text-primary)',
                          pointerEvents: isOfficer ? 'none' : 'auto',
                        }}
                      >
                        <option value="">To</option>
                        {getShiftTimeOptions().map(v => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                    )}
                    {!isOfficer && (
                      <button
                        onClick={() => removePost(i)}
                        style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontWeight: 700 }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Secondary Tasks */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', display: 'block', marginBottom: 8 }}>
              Secondary Tasks
            </label>

            {/* Assigned tasks */}
            {myTasks.length > 0 && (
              <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {myTasks.map((t: TaskEntry, i: number) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: '#f2d7bb',
                      borderRadius: 8,
                      padding: '6px 10px',
                    }}
                  >
                    <span style={{ fontSize: 16 }}>📌</span>
                    <span style={{ fontWeight: 600, fontSize: 13, flex: 1, color: '#1f2937' }}>
                      {t.label}{t.time ? ` @ ${t.time}` : ''}
                    </span>
                    <button
                      onClick={() => removeTask(t.label)}
                      style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontWeight: 700 }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add task picker */}
            {SECONDARY_TASKS.map((taskDef, i) => {
              const assigned = myTasks.some((t: TaskEntry) => t.label === taskDef.label)
              if (assigned) return null
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '5px 10px',
                    borderRadius: 6,
                    marginBottom: 4,
                    background: 'var(--bg-elevated)',
                  }}
                >
                  <span style={{ fontSize: 13, flex: 1, color: 'var(--text-muted)' }}>{taskDef.label}</span>
                  <input
                    type="text"
                    placeholder="HH:MM"
                    id={`task-time-${i}`}
                    defaultValue={autoTaskTimeByLabel(taskDef.label)}
                    style={{
                      width: 64,
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      padding: '2px 6px',
                      fontSize: 12,
                      textAlign: 'center',
                      background: 'var(--bg-deep)',
                      color: 'var(--text-primary)',
                    }}
                  />
                  <button
                    onClick={() => {
                      const el = document.getElementById(`task-time-${i}`) as HTMLInputElement | null
                      const raw = el?.value?.trim() ?? ''
                      const time = raw.match(/^\d{4}$/) ? raw.slice(0, 2) + ':' + raw.slice(2) : raw
                      addTask(taskDef, time)
                    }}
                    style={{
                      padding: '3px 10px',
                      background: '#4f46e5',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    + Add
                  </button>
                </div>
              )
            })}
          </div>

          {/* Shift Time — read-only for officers */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', display: 'block', marginBottom: 8 }}>
              Shift Time {isOfficer && <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(view only)</span>}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
              <input
                type="text"
                inputMode="numeric"
                placeholder="HH:MM"
                maxLength={5}
                value={shiftFrom}
                onChange={e => !isOfficer && handleShiftFrom(e.target.value)}
                readOnly={isOfficer}
                style={{ ...inp, width: 100, textAlign: 'center', cursor: isOfficer ? 'default' : 'text' }}
              />
              <span style={{ color: 'var(--text-muted)', fontWeight: 700 }}>to</span>
              <input
                type="text"
                inputMode="numeric"
                placeholder="HH:MM"
                maxLength={5}
                value={shiftTo}
                onChange={e => !isOfficer && handleShiftTo(e.target.value)}
                readOnly={isOfficer}
                style={{ ...inp, width: 100, textAlign: 'center', cursor: isOfficer ? 'default' : 'text' }}
              />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: 11,
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              color: 'var(--text-primary)',
            }}
          >
            Cancel
          </button>
          <button
            onClick={save}
            style={{
              flex: 2,
              padding: 11,
              background: '#1d4ed8',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              color: '#fff',
            }}
          >
            Save Equipment
          </button>
        </div>
      </div>
    </div>
  )
}
